import React, { useState, useEffect } from 'react';
import { apiRoutes } from 'api_routes/api_routes';
import styled from 'styled-components';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuIcon from '@mui/icons-material/Menu';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import CreateIcon from '@mui/icons-material/Create';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Logo from 'components/atoms/Logo/Logo';
import Breadcrumbs from 'components/atoms/Breadcrumbs/Breadcrumbs';
import ButtonIcon from 'components/atoms/ButtonIcon/ButtonIcon';
import { NavLink, useLocation } from 'react-router-dom';
import logoutIcon from 'assets/icons/logout.svg';
import axiosInstance from 'api/axios';
import LangswitcherWithHelp from 'components/atoms/Langswitcher/LangswitcherWithHelp';
import { useTranslation } from 'react-i18next';

const drawerWidth = 200;

const Logout = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledLogoutButton = styled(ButtonIcon)`
  position: fixed;
  bottom: 0;
  margin-bottom: 0.5rem;
`;

const DrawerWrapper = styled.div`
  height: 100vh;
`;

const BreadcrumbsWrapper = styled.div`
  padding: 0 15px;
`;

const ResponsiveDrawer = ({ pageType, title, ...props }) => {
  const { window } = props;
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [path, setPath] = useState('');
  const location = useLocation();
  const menuList = [
    {
      path: '/user',
      sidebarName: t('user_data'),
      icon: PersonIcon,
    },
    {
      path: '/slips',
      sidebarName: t('payment'),
      icon: FolderIcon,
    },
    {
      path: '/pits',
      sidebarName: t('pit_ift_declarations'),
      icon: FolderIcon,
    },
    {
      path: '/contact_form',
      sidebarName: t('contact_form'),
      icon: CreateIcon,
    },
  ];

  useEffect(() => {
    setPath(location.pathname);
  }, [location, setPath]);

  const activetRoute = (route) => route === path;

  const onLogoutClick = () => {
    axiosInstance
      .get(apiRoutes.logout)
      .then((response) => {})
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 302) {
          localStorage.setItem('user', '');
          localStorage.setItem('roles', JSON.stringify([]));
        }
      });
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <DrawerWrapper>
      <Toolbar>
        <Logo />
      </Toolbar>

      <List>
        {menuList.map((item, key) => (
          <ListItem key={key} selected={activetRoute(item.path)} disablePadding>
            <ListItemButton
              as={NavLink}
              to={item.path}
              sx={{
                color: 'black',
                ':hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <ListItemIcon>
                <item.icon fontSize="large" sx={{ color: 'black' }} />
              </ListItemIcon>
              <ListItemText
                primary={item.sidebarName}
                sx={{
                  '& span': {
                    fontSize: '1.1rem',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <BreadcrumbsWrapper>
        <Divider />
        <Breadcrumbs>
          <span>TOKEN:</span> {t('token_definition')} {t('token_definition_disclaimer')}
        </Breadcrumbs>
        <Divider />
      </BreadcrumbsWrapper>
      <Logout>
        <StyledLogoutButton as={NavLink} to="/login" icon={logoutIcon} onClick={onLogoutClick} />
      </Logout>
    </DrawerWrapper>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ backgroundColor: 'hsl(228, 55%, 42%)', paddingLeft: '54px' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, marginLeft: '-35px' }}
          >
            <MenuIcon />
          </IconButton>
          <LangswitcherWithHelp linkColor="drawer" btn="outlined" />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              height: 'calc(100vh - 55px)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
};

export default ResponsiveDrawer;
